import type { Action } from "app/services/types.generated";

export interface NavigateToArgs {
    pose: {
        position: { x: number; y: number; z: number };
        orientation: { x: number; y: number; z: number; w: number };
    };
    map_resource_id?: string;
}

export interface MapWaypoint {
    id: string;
    pose: NavigateToArgs["pose"];
}

const hasValidPose = (args: unknown): args is NavigateToArgs => {
    if (!args || typeof args !== "object") return false;
    const pose = (args as NavigateToArgs).pose;
    const position = pose?.position;
    return (
        Number.isFinite(position?.x) &&
        Number.isFinite(position?.y)
    );
};

export const navigateToActionsToMapWaypoints = (actions: Action[]): MapWaypoint[] => {
    return actions
        .filter((action) => action.actionType === "NavigateTo" && hasValidPose(action.args))
        .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
        .map((action) => ({
            id: action.id,
            pose: (action.args as NavigateToArgs).pose,
        }));
};

/** 1-based NavigateTo order in mission sequence (matches map pin badges). */
export const buildNavigateToOrdinalMap = (actions: Action[]): Map<string, number> => {
    const ordinals = new Map<string, number>();
    let n = 0;
    for (const action of [...actions].sort((a, b) => a.sequenceOrder - b.sequenceOrder)) {
        if (action.actionType !== "NavigateTo") continue;
        n += 1;
        ordinals.set(action.id, n);
    }
    return ordinals;
};

/** Non-navigation mission items shown on the map with their own icon. */
export const MAP_MARKER_ACTION_TYPES = new Set([
    "TakePhoto",
    "Record360",
    "Wait",
    "WalkStair",
    "Docking",
    "Dock",
    "Undock",
]);

export interface MissionMapActionMarker {
    id: string;
    actionType: string;
    /** Anchor pose: the most recent NavigateTo before this item in the sequence. */
    pose: NavigateToArgs["pose"];
    /** Stacking slot at the same anchor; 0 is reserved for the NavigateTo pin itself. */
    stackIndex: number;
}

/**
 * Map markers for photo / 360 / wait / stair / dock items. These actions store
 * no pose, so each anchors to the nearest preceding NavigateTo waypoint (or the
 * first waypoint when it happens before any navigation).
 */
export const missionActionsToMapMarkers = (actions: Action[]): MissionMapActionMarker[] => {
    const sorted = [...actions].sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    const firstWaypointPose = sorted.find(
        (action) => action.actionType === "NavigateTo" && hasValidPose(action.args),
    );
    if (!firstWaypointPose) return [];
    const firstPose = (firstWaypointPose.args as NavigateToArgs).pose;

    const stackByKey = new Map<string, number>();
    const markers: MissionMapActionMarker[] = [];
    let lastPose: NavigateToArgs["pose"] | null = null;

    for (const action of sorted) {
        if (action.actionType === "NavigateTo") {
            if (hasValidPose(action.args)) {
                lastPose = (action.args as NavigateToArgs).pose;
            }
            continue;
        }
        if (!MAP_MARKER_ACTION_TYPES.has(action.actionType)) continue;
        const pose = lastPose ?? firstPose;
        const key = `${pose.position.x},${pose.position.y}`;
        const stackIndex = (stackByKey.get(key) ?? 0) + 1;
        stackByKey.set(key, stackIndex);
        markers.push({ id: action.id, actionType: action.actionType, pose, stackIndex });
    }
    return markers;
};
